import { Alert, Button, Space, Typography } from 'antd';
import { space } from '@viq-admin/ui';
import { CRM_COPY } from './copy';
import { HelpHint } from './HelpHint';
import type { MergePolicy } from './types';

export interface SendActionRailProps {
  effectivePolicy: MergePolicy;
  summaryText: string | null;
  needsEmailCount: number;
  integrationAccountOwnedCount: number;
  ownerUnverifiedCount: number;
  ownersUnavailable: boolean;
  sendDisabledReason: string | null;
  busy: boolean;
  onSend: () => void;
}

/**
 * The Send tab's action rail — policy line, selection summary, the four
 * above-the-button disclosures, and the Send button itself, in that reading
 * order so the button reads last, under the facts it commits to. Pure
 * presentation: every value it renders is already computed by `SendView`
 * (or its future replacement) and handed down as a prop.
 */
export function SendActionRail({
  effectivePolicy,
  summaryText,
  needsEmailCount,
  integrationAccountOwnedCount,
  ownerUnverifiedCount,
  ownersUnavailable,
  sendDisabledReason,
  busy,
  onSend,
}: SendActionRailProps) {
  // Derived here rather than taken as a prop — it is a function of the four
  // counts above, and two sources for one truth is how they drift.
  const hasDisclosures =
    needsEmailCount > 0 || integrationAccountOwnedCount > 0 || ownerUnverifiedCount > 0 || ownersUnavailable;

  return (
    <>
      {/* The policy is chosen at the top of Match (gh-31 §4.7); here it is
          restated in one line so the operator sees what this send will do
          without leaving the tab. */}
      <Typography.Paragraph
        type="secondary"
        data-testid="crm-send-policy-line"
        style={{ marginBottom: space.sm }}
      >
        {`${CRM_COPY.policyOnSendPrefix} ${
          effectivePolicy === 'overwrite' ? CRM_COPY.policyOverwrite : CRM_COPY.policyFillBlanks
        }. ${CRM_COPY.policyOnSendSuffix}`}
      </Typography.Paragraph>

      {summaryText && (
        <Space size={space.xs} align="center" style={{ display: 'flex', marginBottom: space.sm }}>
          <Typography.Text>{summaryText}</Typography.Text>
          <HelpHint {...CRM_COPY.help.sendSummary} testId="crm-help-send-summary" />
        </Space>
      )}

      {/* p3 Task 4 — spec 1 §3 step 5 "reported, never silent" / §4
          resolution (b). All four are informational; none of them may
          disable Send (see `sendDisabledReason` above, which never reads
          any of these). Zero/absent counts render nothing, same convention
          `buildSummary` follows above. `ownersUnavailable` renders first and
          as an Alert rather than a plain line — it says the owner facts
          below (and the integration-account line right after it, when both
          appear) can't be trusted, so it has to read as categorically
          different from them, not as a fourth fact alongside them. */}
      {hasDisclosures && (
        <Space
          direction="vertical"
          size={space.xs}
          style={{ display: 'flex', marginBottom: space.sm }}
        >
          {ownersUnavailable && (
            <Alert
              type="warning"
              showIcon
              message={CRM_COPY.sendDisclosures.ownersUnavailable}
              data-testid="crm-send-disclosure-owners-unavailable"
            />
          )}
          {needsEmailCount > 0 && (
            <Typography.Text type="secondary" data-testid="crm-send-disclosure-needs-email">
              {CRM_COPY.sendDisclosures.needsEmail(needsEmailCount)}
            </Typography.Text>
          )}
          {integrationAccountOwnedCount > 0 && (
            <Typography.Text
              type="secondary"
              data-testid="crm-send-disclosure-integration-account"
            >
              {CRM_COPY.sendDisclosures.integrationAccountOwned(integrationAccountOwnedCount)}
            </Typography.Text>
          )}
          {ownerUnverifiedCount > 0 && (
            <Typography.Text type="secondary" data-testid="crm-send-disclosure-owner-unverified">
              {CRM_COPY.sendDisclosures.ownerUnverified(ownerUnverifiedCount)}
            </Typography.Text>
          )}
        </Space>
      )}

      <Space size={space.sm} align="center" wrap>
        {/* Driven by the same value that names the reason, so the button and
            the explanation beside it can never disagree about why. */}
        <Button
          type="primary"
          disabled={sendDisabledReason !== null || busy}
          loading={busy}
          onClick={onSend}
        >
          {CRM_COPY.sendButton}
        </Button>
        {/* Said in plain view rather than in a tooltip: a disabled antd Button
            can't take focus, so a tooltip on it would be reachable by pointer
            only — and this is the one explanation an operator staring at a
            dead Send button actually needs. `busy` is left unexplained; the
            button's own spinner already says it. */}
        {sendDisabledReason && (
          <Typography.Text type="secondary" data-testid="crm-send-disabled-reason">
            {sendDisabledReason}
          </Typography.Text>
        )}
      </Space>
    </>
  );
}
